import './bootstrap';

import { createApp, ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
import _ from 'lodash';
import FlushMessage from './components/FlushMessage.vue';
import UploadFormComponent from './components/UploadFormComponent.vue';
import DateTimeComponent from './components/DateTimeComponent.vue';
import TagAutoInputComponent from './components/TagAutoInputComponent.vue';
import AiAttributeFilterComponent from './components/AiAttributeFilterComponent.vue';
import UserSelectComponent from './components/UserSelectComponent.vue';
import MultiUploadFormComponent from './components/MultiUploadFormComponent.vue';
import KindUploadListComponent from './components/KindUploadListComponent.vue';
import Player from './components/Player.vue';
import ClipCreatorComponent from './components/ClipCreatorComponent.vue';
import ClipModalComponent from './components/ClipModalComponent.vue';
import ClipShowModalComponent from './components/ClipShowModalComponent.vue';
import ExpireDateComponent from './components/ExpireDateComponent.vue';
import AreaTagComponent from './components/AreaTagComponent.vue';
import ShareUrlComponent from './components/ShareUrlComponent.vue';
import Videojs from './components/Videojs.vue';
import ThumbnailSelector from './components/ThumbnailSelector.vue';
import FastDownload from './components/FastDownload.vue';
import PdfPreviewPdfjs from './components/PdfPreviewPdfjs.vue';
import CadViewerAps from './components/CadViewerAps.vue';
import SharePreviewModal from './components/SharePreviewModal.vue';
import FolderNode from './components/FolderNode.vue';
import FolderPickerTree from './components/FolderPickerTree.vue';
import FolderListTable from './components/FolderListTable.vue';
import VersionHistoryTab from './components/VersionHistoryTab.vue';
import GridTagBadges from './components/GridTagBadges.vue';
import FolderArchivePanel from './components/FolderArchivePanel.vue';
import HighlightedText from './components/HighlightedText.vue';
import { useViewMode } from './dashboard/useViewMode';
import { useAttachmentTarget } from './dashboard/useAttachmentTarget';
import { useFolderTree } from './dashboard/useFolderTree';
import { useSelection } from './dashboard/useSelection';
import { useDataSource } from './dashboard/useDataSource';
import { useFolderNavigation, FAVORITES_FOLDER_ID } from './dashboard/useFolderNavigation';
import { useDomEnhancements } from './dashboard/useDomEnhancements';
import { useRestoreIdSearch } from './dashboard/useRestoreIdSearch';
import { useDragAndDrop } from './dashboard/useDragAndDrop';
import { useFavoriteFilters } from './dashboard/useFavoriteFilters';
import { useTopFilter } from './dashboard/useTopFilter';
import { useFolderPin } from './dashboard/useFolderPin';
import { useExportActions } from './dashboard/useExportActions';
import { useVersionActions } from './dashboard/useVersionActions';
import { useAiAnalysis } from './dashboard/useAiAnalysis';
import { usePaneLayout } from './dashboard/usePaneLayout';
import { useRestoreBarLayout } from './dashboard/useRestoreBarLayout';
import { useToolbarOverflow } from './dashboard/useToolbarOverflow';
import { useListHeaderSync } from './dashboard/useListHeaderSync';
import { useDetailExpand } from './dashboard/useDetailExpand';
import { useShareBasket } from './dashboard/useShareBasket';
import { useUnifiedRangeSelect } from './dashboard/useUnifiedRangeSelect';
import { useDirectLinkCopy } from './dashboard/useDirectLinkCopy';
import { useNavDrawer } from './dashboard/useNavDrawer';
import { useFolderCreate, sortFoldersByHierarchy } from './dashboard/useFolderCreate';
import { useVideoFormFolderPicker } from './dashboard/useVideoFormFolderPicker';
import { useFolderBulkDelete } from './dashboard/useFolderBulkDelete';
import { useTrashRestore } from './dashboard/useTrashRestore';
import { useClipTrashRestore } from './dashboard/useClipTrashRestore';
import { useBulkTrash } from './dashboard/useBulkTrash';
import { useSelectionArchive } from './dashboard/useSelectionArchive';
import SelectionArchiveList from './components/SelectionArchiveList.vue';
import { useSourceDelete } from './dashboard/useSourceDelete';
import { useBulkTags } from './dashboard/useBulkTags';
import { useBulkAiAnalysis } from './dashboard/useBulkAiAnalysis';
import { useSearchMatches } from './dashboard/useSearchMatches';
import { useDetailPreviewPage } from './dashboard/useDetailPreviewPage';
import { useBodyText } from './dashboard/useBodyText';
import { useCadInfo } from './dashboard/useCadInfo';
import { useDetailPaneJump } from './dashboard/useDetailPaneJump';
import { filterVideoItems } from './dashboard/useItemTypeGuards';
import { bootPageModules } from './pages';

const app = createApp({
  setup() {
    const uploadingKeys = ref(new Set());
    const isUploading = computed(() => uploadingKeys.value.size > 0);
    const isSubmit = ref(false);
    const showSidebar = ref(false);
    const isMobileSearchOpen = ref(false);
    const favoritesFolderLabel = ref('お気に入り');
    const isFavoritesFolderId = (folderId) =>
      String(folderId ?? '') === FAVORITES_FOLDER_ID;

    const {
      items,
      folders,
      allowedFolderIds,
      selectedFolderId,
      searchScope,
      hasSearchConditions,
      itemsTotal,
      folderSearchCount,
      focusQuery,
      activeKeyword,
      restoreCounts: initialRestoreCounts,
      currentUserId,
      canUseFulltextSearch,
      folderCountLabelTemplate,
      fileCountLabelTemplate,
      maxInputVars,
      bulkMaxItems,
      initDataSource,
    } = useDataSource();
    const { viewMode, setViewMode, initViewMode } = useViewMode();
    const {
      attachmentTargetOptions,
      attachmentTargetFilename,
      attachmentTargetAutoLabel,
      setAttachmentTargetOptions,
      setAttachmentCount,
      showAttachmentTargetSelect,
      initAttachmentTarget,
    } = useAttachmentTarget();
    const filterPanelOpen = ref(false);

    const {
      selectedItem: selectedItemRef,
      selectedIds,
      selectItem,
      toggleItemSelection,
      applyRangeSelection,
      restoreSelection,
      clearSelection,
      storeFolderSelection,
    } = useSelection(items, folders, selectedFolderId);

    // useDomEnhancements の検索範囲自動切替が TOP 表示かを参照するため、その前に作る
    const { topOnly, topFolderLabel, initTopPreference, syncTopOnly } =
      useTopFilter();

    const { init: initDomEnhancements, syncFolderIdFields } = useDomEnhancements({
      selectedFolderId,
      hasSearchConditions,
      topOnly,
    });
    watch(selectedFolderId, () => {
      syncFolderIdFields(searchScope.value);
    });

    const {
      basketCount: shareBasketCount,
      basketVideoIds: shareBasketVideoIds,
      basketFolderIds: shareBasketFolderIds,
      basketFileCount: shareBasketFileCount,
      basketFolderCount: shareBasketFolderCount,
      basketItemsList: shareBasketItemsList,
      basketFileItemsList: shareBasketFileItemsList,
      toggleBasket: toggleShareBasket,
      isInBasket: isInShareBasket,
      clearBasket: clearShareBasket,
      removeFromBasket: removeFromShareBasket,
      setBasketItems: setShareBasketItems,
      addToBasket,
    } = useShareBasket(folders, currentUserId);

    const detailSubTab = ref('info');
    const detailPlayerRef = ref(null);

    const setItemsTotal = (value) => {
      const parsed = Number(value);
      if (Number.isFinite(parsed) && parsed >= 0) {
        itemsTotal.value = Math.trunc(parsed);
        return;
      }

      const list = Array.isArray(items.value) ? items.value : [];
      itemsTotal.value = list.length;
    };

    const setFolderSearchCount = (value) => {
      const parsed = Number(value);
      folderSearchCount.value = Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : 0;
    };

    const setActiveKeyword = (value) => {
      activeKeyword.value = typeof value === 'string' ? value : '';
    };

    const restoreCountsState = ref({
      global: {
        restoring: { count: 0, display_ids: [], more: 0 },
        restored: { count: 0, display_ids: [], more: 0 },
        full_restoring: { count: 0, display_ids: [], more: 0 },
        full_restored: { count: 0, display_ids: [], more: 0 },
        full_restore_failed: { count: 0, display_ids: [], more: 0 },
      },
      current: {
        restoring: { count: 0, display_ids: [], more: 0 },
        restored: { count: 0, display_ids: [], more: 0 },
        full_restoring: { count: 0, display_ids: [], more: 0 },
        full_restored: { count: 0, display_ids: [], more: 0 },
        full_restore_failed: { count: 0, display_ids: [], more: 0 },
      },
    });
    const applyRestoreCounts = (payload) => {
      const normalizeState = (state) => ({
        count: Number(state?.count || 0),
        display_ids: Array.isArray(state?.display_ids) ? state.display_ids : [],
        more: Number(state?.more || 0),
      });
      restoreCountsState.value = {
        global: {
          restoring: normalizeState(payload?.global?.restoring),
          restored: normalizeState(payload?.global?.restored),
          full_restoring: normalizeState(payload?.global?.full_restoring),
          full_restored: normalizeState(payload?.global?.full_restored),
          full_restore_failed: normalizeState(payload?.global?.full_restore_failed),
        },
        current: {
          restoring: normalizeState(payload?.current?.restoring),
          restored: normalizeState(payload?.current?.restored),
          full_restoring: normalizeState(payload?.current?.full_restoring),
          full_restored: normalizeState(payload?.current?.full_restored),
          full_restore_failed: normalizeState(payload?.current?.full_restore_failed),
        },
      };
    };

    const currentFolderItemCount = computed(() => {
      const parsed = Number(itemsTotal.value);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
      return Array.isArray(items.value) ? items.value.length : 0;
    });
    const currentFolderItemCountLabel = computed(() => {
      const fc = Number(folderSearchCount.value);
      const fileLabel = (fileCountLabelTemplate.value || '').replace(
        '__COUNT__',
        String(currentFolderItemCount.value)
      );
      if (fc > 0) {
        const folderLabel = (folderCountLabelTemplate.value || '').replace('__COUNT__', String(fc));
        return `${folderLabel} / ${fileLabel}`;
      }
      return fileLabel;
    });

    const {
      favoriteOnly,
      initFavoritePreference,
      removeDashboardParamsFromAddressBar,
      toggleSeqOrder,
      setFavoriteOnly,
      toggleFavorite,
      isFavoriteToggling,
      seqOrder,
      sortKey,
      setSortOrder,
    } = useFavoriteFilters({
      items,
      selectedItemRef,
      selectItem,
      currentFolderItemCount,
      setItemsTotal,
      selectedFolderId,
      searchScope,
    });

    const {
      pinnedFolderIds,
      pinnedFolders,
      initFolderPins,
      applyPinnedFolderIds,
      toggleFolderPin,
      isPinProcessing,
    } = useFolderPin({
      folders,
      allowedFolderIds,
    });
    const pinnedFolderSectionStyle = computed(() => ({
      '--pinned-folder-count': String(Math.max(1, pinnedFolders.value.length)),
    }));

    const fetchDetailStatus = (item) => {
      if (!item || !item.detail_status_url) {
        return;
      }
      // HOT化済み(is_upload_file_archive=false)でも、完全復元中バッジが残っている場合は
      // 1回ポーリングしてバッジを解除する。それ以外のHOT化済みアイテムはスキップ。
      if (!item.is_upload_file_archive
          && !(item.is_full_restore && item.restore_status === 'restoring')) {
        return;
      }
      window.axios.get(item.detail_status_url)
        .then((response) => {
          if (response.data) {
            // D2: restore_status は null でも反映する(HOT化後のバッジ解除のため)
            if ('restore_status' in response.data) {
              item.restore_status = response.data.restore_status;
            }
            // HOT→COLD の Lifecycle 待ち窓 (実体 GLACIER_IR) を archive=false に同期し手詰まり解消
            if ('is_upload_file_archive' in response.data) {
              item.is_upload_file_archive = response.data.is_upload_file_archive;
            }
            if (response.data.restore_expires_at !== undefined) {
              item.restore_expires_at = response.data.restore_expires_at;
            }
            // D1: 完全復元フィールドを item に反映
            if ('is_full_restore' in response.data) {
              item.is_full_restore = response.data.is_full_restore;
            }
            if ('failure_reason' in response.data) {
              item.failure_reason = response.data.failure_reason;
            }
            if ('early_deletion_remaining_days' in response.data) {
              item.early_deletion_remaining_days = response.data.early_deletion_remaining_days;
            }
            if ('is_shareable' in response.data) {
              item.is_shareable = response.data.is_shareable;
            }
            if ('is_cad_previewable' in response.data) {
              item.is_cad_previewable = response.data.is_cad_previewable;
            }
            if ('cad_status' in response.data) {
              item.cad_status = response.data.cad_status;
            }
            if ('cad_ready' in response.data) {
              item.cad_ready = response.data.cad_ready;
            }
            if ('transcode_status' in response.data) {
              item.transcode_status = response.data.transcode_status;
            }
            if ('transcode_error' in response.data) {
              item.transcode_error = response.data.transcode_error;
            }
          }
        })
        .catch(() => {});
    };

    const {
      clearKeywordOnNextFolderSelect,
      applyFocusedVideoSelectionFromUrl,
      hasFocusIdInUrl,
      hasFolderIdInUrl,
      searchByRestoreId,
    } = useRestoreIdSearch({
      items,
      detailSubTab,
      selectItem,
      fetchDetailStatus,
      storeFolderSelection,
      clearBasket: clearShareBasket,
      addToBasket,
      focusQuery,
    });

    const { selectFolder: fetchFolderItems, reloadCurrentFolder } = useFolderNavigation({
      items,
      selectedFolderId,
      storeFolderSelection,
      onFolderDataLoaded: (responseData) => {
        applyRestoreCounts(responseData?.restore_counts);
        setItemsTotal(responseData?.items_total);
        if (responseData && Object.prototype.hasOwnProperty.call(responseData, 'ai_attribute_counts')) {
          document.dispatchEvent(
            new CustomEvent('dashboard-ai-attribute-counts', { detail: responseData.ai_attribute_counts })
          );
        }
        setFolderSearchCount(responseData?.folder_search_count);
        setActiveKeyword(responseData?.active_keyword);
        applyPinnedFolderIds(responseData?.pinned_folder_ids);
        currentFolderPermissions.value = responseData?.current_folder_permissions || [];
        syncTopOnly(responseData?.top);
        const scope = responseData?.search_scope || 'folder';
        searchScope.value = scope;
        hasSearchConditions.value = !!responseData?.has_search_conditions;
        document.querySelectorAll('input[name="search_scope"]').forEach((h) => {
          h.value = scope;
        });
        document.querySelectorAll('.js-search-scope-option').forEach((btn) => {
          const isActive = btn.dataset.scope === scope;
          btn.classList.toggle('btn-primary', isActive);
          btn.classList.toggle('btn-outline-secondary', !isActive);
        });
      },
      clearKeywordOnNextSelect: clearKeywordOnNextFolderSelect,
    });

    // restore-bar高さのCSS変数をスクロール前に同期反映できるよう、useFolderTreeより先に取得する
    const { initRestoreBarLayout, teardownRestoreBarLayout, syncRestoreBarHeight } =
      useRestoreBarLayout();
    const { initToolbarOverflow, teardownToolbarOverflow, checkToolbarOverflow } = useToolbarOverflow();
    const { initListHeaderSync, teardownListHeaderSync } = useListHeaderSync();

    const {
      topLevelFolders,
      getChildren,
      folderBreadcrumbItems,
      isCollapsed,
      toggleFolder,
      expandAllFolders,
      collapseAllFolders,
      expandAncestorsOf,
      isAllFoldersCollapsed,
      toggleAllFolders,
      getFolderPathItems,
      getFolderPath,
    } = useFolderTree(folders, selectedFolderId, {
      autoRevealSelection: true,
      beforeScroll: syncRestoreBarHeight,
    });

    // フォルダ選択チェックボックスの表示可否。FolderNode.vue/useDragAndDrop.jsと同じ判定
    // (allowedFolderIds===nullは全許可=管理者)
    const isFolderAllowed = (folderId) => {
      if (allowedFolderIds.value === null) {
        return true;
      }
      const numericId = Number(folderId);
      return (allowedFolderIds.value || []).some((id) => Number(id) === numericId);
    };

    const { computeShiftRange, setAnchorFromItem } = useUnifiedRangeSelect({
      items,
      getFolderPath,
      selectedItem: selectedItemRef,
      selectedFolderId,
      isFolderAllowed,
    });

    const currentFolderPermissions = ref([]);

    const currentFolderMeta = computed(() => {
      const folderId = selectedFolderId.value;
      if (folderId === null || folderId === undefined) return null;
      const node = (folders.value || []).find(f => String(f.id) === String(folderId));
      return node || null;
    });

    const activeBreadcrumbItems = computed(() => {
      if (favoriteOnly.value) {
        return [
          {
            id: null,
            name: favoritesFolderLabel.value,
            clickable: false,
          },
        ];
      }

      if (topOnly.value) {
        return [
          {
            id: null,
            name: topFolderLabel.value,
            clickable: false,
          },
        ];
      }

      return (folderBreadcrumbItems.value || []).map((item, index, array) => ({
        id: item.id,
        name: item.name,
        clickable: index < array.length - 1,
      }));
    });
    const activeBreadcrumb = computed(() =>
      activeBreadcrumbItems.value.map((item) => item.name)
    );
    const mobileCurrentFolderName = computed(() => {
      const list = activeBreadcrumbItems.value || [];
      if (!list.length) {
        return favoritesFolderLabel.value || 'フォルダ';
      }
      return list[list.length - 1]?.name || favoritesFolderLabel.value || 'フォルダ';
    });
    const MOBILE_BREADCRUMB_MAX_LENGTH = 32;
    const mobileBreadcrumbDisplay = computed(() => {
      const list = (activeBreadcrumbItems.value || [])
        .map((item) => String(item?.name ?? '').trim())
        .filter(Boolean);

      // `mobileCurrentFolderName` で現在階層は表示済みのため、ここでは祖先階層を扱う。
      if (list.length <= 1) {
        return '';
      }

      const firstLevel = list[0];
      const secondLevelAndBelow = list.slice(1, -1);
      if (!secondLevelAndBelow.length) {
        return firstLevel;
      }

      const ancestorTrail = [firstLevel, ...secondLevelAndBelow];
      const fullDisplay = ancestorTrail.join(' / ');
      const shouldCompact =
        fullDisplay.length > MOBILE_BREADCRUMB_MAX_LENGTH || ancestorTrail.length > 2;

      if (!shouldCompact) {
        return fullDisplay;
      }

      const lastAncestor = secondLevelAndBelow[secondLevelAndBelow.length - 1];
      const compactDisplay = lastAncestor
        ? `${firstLevel} / ... / ${lastAncestor}`
        : `${firstLevel} / ...`;

      if (compactDisplay.length <= MOBILE_BREADCRUMB_MAX_LENGTH) {
        return compactDisplay;
      }

      return `${firstLevel} / ...`;
    });
    const currentFolderId = computed(() => {
      if (favoriteOnly.value) {
        return null;
      }

      const parsed = Number(selectedFolderId.value);
      if (!Number.isFinite(parsed)) {
        return null;
      }

      const normalizedId = Math.trunc(parsed);
      if (normalizedId <= 0) {
        return null;
      }

      return normalizedId;
    });
    const canToggleCurrentFolderPin = computed(
      () => currentFolderId.value !== null
    );
    const isCurrentFolderPinned = computed(() => {
      if (currentFolderId.value === null) {
        return false;
      }

      return pinnedFolderIds.value.some(
        (folderId) => String(folderId) === String(currentFolderId.value)
      );
    });
    const isCurrentFolderPinProcessing = computed(() => {
      if (currentFolderId.value === null) {
        return false;
      }

      return isPinProcessing(currentFolderId.value);
    });
    const toggleCurrentFolderPin = async () => {
      if (currentFolderId.value === null) {
        return;
      }

      await toggleFolderPin(currentFolderId.value);
    };

    // フォルダクリックで詳細パネルに表示するフォルダノード(動画選択とは別枠)
    const selectedFolderNode = ref(null);

    // 選択リストから外れたら詳細パネルは開いているフォルダの情報表示に戻す
    watch(shareBasketFolderIds, (next, prev) => {
      if (!selectedFolderNode.value) {
        return;
      }
      const id = Number(selectedFolderNode.value.id);
      if (prev.includes(id) && !next.includes(id)) {
        selectedFolderNode.value = null;
      }
    });

    // TOP(root仮想フォルダ)はお気に入りと異なり専用の文字列IDを持たず、folderId===nullで表す
    const isTopFolderId = (folderId) => folderId === null;

    const selectFolder = async (folderId) => {
      selectedFolderNode.value = null;
      if (isFavoritesFolderId(folderId)) {
        const updated = await setFavoriteOnly(true);
        if (!updated) {
          return;
        }
        await fetchFolderItems(FAVORITES_FOLDER_ID);
        isMobileSearchOpen.value = false;
        return;
      }

      if (isTopFolderId(folderId)) {
        const updated = await setFavoriteOnly(false);
        if (!updated) {
          return;
        }
        await fetchFolderItems(null);
        isMobileSearchOpen.value = false;
        return;
      }

      const parsed = Number(folderId);
      if (!Number.isFinite(parsed)) {
        return;
      }
      const normalizedFolderId = Math.trunc(parsed);
      if (normalizedFolderId <= 0) {
        return;
      }

      const updated = await setFavoriteOnly(false);
      if (!updated) {
        return;
      }
      await fetchFolderItems(normalizedFolderId);
      isMobileSearchOpen.value = false;
    };

    const selectFavoritesFolder = async () => {
      await selectFolder(FAVORITES_FOLDER_ID);
    };

    const selectTopFolder = () => selectFolder(null);

    const { initPaneLayout, teardownPaneLayout } = usePaneLayout();
    const { isDetailExpanded, toggleDetailExpand, closeDetailExpand } =
      useDetailExpand();

    const {
      moveFailedMessage,
      isDragging,
      dragTargetFolderId,
      isTopDropTarget,
      noDropFolderIds,
      setMoveOptions,
      handleDragStart,
      handleDragEnd,
      handleFolderDrop,
      handleFolderDragEnter,
      handleFolderDragLeave,
      handleTopDrop,
      handleTopDragEnter,
      handleTopDragLeave,
    } = useDragAndDrop({
      items,
      isInShareBasket,
      shareBasketVideoIds,
      setShareBasketItems,
      selectItem: (item, index) => {
        selectItem(item, index);
        setAnchorFromItem(item);
      },
      clearSelection,
      reloadCurrentFolder,
      selectedFolderId,
      allowedFolderIds,
      removeFromShareBasket,
      folders,
      shareBasketFolderIds,
    });

    const {
      attachmentArchives,
      openAttachmentDownloadModal,
      downloadAllArchives,
      exportToOneDrive,
      exportToSharePoint,
      exportToGoogleDrive,
      exportToDropbox,
      exportToBox,
      isExporting,
    } = useExportActions();

    const {
      flashMessage: directLinkFlashMessage,
      flashIsError: directLinkFlashIsError,
      flashKey: directLinkFlashKey,
      copyFileLink: copyDirectLinkFile,
      copyFolderLink: copyDirectLinkFolder,
    } = useDirectLinkCopy();

    const {
      deleteTargetItem,
      deleteMode,
      deleteVersionsList,
      deleteVersionsLoading,
      showDeleteModal,
      handleVersionSelect,
      confirmDelete,
    } = useVersionActions({
      items,
      selectedFolderId,
      selectFolder,
      selectItem,
      detailSubTab,
      removeFromShareBasket,
    });

    const {
      aiAnalysisData,
      aiAnalysisLoading,
      aiAnalysisError,
      fetchAiAnalysis,
      formatSeconds: formatAiSeconds,
      sceneThumbnailPreviewUrl,
      openSceneThumbnailPreview,
      seekToTranscriptSegment,
    } = useAiAnalysis({ selectedItem: selectedItemRef, detailPlayerRef });

    // useDetailPaneJump は useBodyText の ensureBodyPageLoaded を必要とするが、
    // useBodyText は useSearchMatches の applyPageNumber を必要とするため生成順が
    // 循環する (useDetailPaneJump → useSearchMatches → useBodyText)。
    // ref を先に用意して渡し、useBodyText 生成後に実体を差し込む
    const ensureBodyPageRef = ref(null);
    // useCadInfo の ensureCadInfoLoaded も同様に、生成後に差し込む
    const ensureCadInfoRef = ref(null);

    const { isJumpableHit, jumpToHit, scrollToCadView } = useDetailPaneJump({
      selectedItem: selectedItemRef,
      detailSubTab,
      aiAnalysisData,
      fetchAiAnalysis,
      ensureBodyPageRef,
      ensureCadInfoRef,
    });

    const ensureDefaultFolder = () => {
      if (favoriteOnly.value) {
        selectedFolderId.value = FAVORITES_FOLDER_ID;
        return;
      }
      if (topOnly.value) {
        selectedFolderId.value = null;
        return;
      }
      if (searchScope.value === 'all') {
        // 「全体」検索モードでは特定フォルダを選択状態にしない。
        // topOnly と同様「フォルダ横断」表示のため、以下の「階層1の先頭フォルダへの
        // 暗黙フォールバック」は不要かつ有害(TOP→全体だけでなく、通常フォルダ→全体の
        // 遷移でも同じ問題が起きていた既存の一般バグ)。
        return;
      }
      if (selectedFolderId.value !== null) {
        return;
      }
      const top = (folders.value || []).find(
        (folder) => String(folder.parent_id ?? 0) === '0'
      );
      if (top) {
        selectedFolderId.value = top.id;
      }
    };

    // shift範囲選択(フォルダ+動画混在)を共通処理として反映する。
    // クリック行が動画ならselectedItem/selectedFolderNodeを動画側に、フォルダなら逆に設定する
    const applyShiftRangeSelection = (clickedRow, { append = false } = {}) => {
      const range = computeShiftRange(clickedRow);
      setShareBasketItems(range, { append });
      const videos = filterVideoItems(range);
      const isClickedFolder = clickedRow && clickedRow.item_type === 'folder';
      applyRangeSelection(videos, { clickedVideo: isClickedFolder ? null : clickedRow, append });

      if (isClickedFolder) {
        const node = (folders.value || []).find((f) => String(f.id) === String(clickedRow.id))
          || { id: clickedRow.id, name: clickedRow.name };
        selectedFolderNode.value = node;
      } else {
        selectedFolderNode.value = null;
      }

      setAnchorFromItem(clickedRow);
    };

    const handleItemClick = (item, index, event) => {
      if (!item) {
        return;
      }
      selectedFolderNode.value = null;
      if (event && event.shiftKey) {
        applyShiftRangeSelection(item, { append: event.ctrlKey || event.metaKey });
        return;
      }
      if (event && (event.ctrlKey || event.metaKey)) {
        toggleItemSelection(item, index);
        toggleShareBasket(item);
        setAnchorFromItem(item);
        return;
      }
      selectItem(item, index);
      fetchDetailStatus(item);
      clearShareBasket();
      toggleShareBasket(item);
      setAnchorFromItem(item);
    };

    const handleCheckboxClick = (item, index, event) => {
      if (!item) {
        return;
      }
      selectedFolderNode.value = null;
      if (event && event.shiftKey) {
        applyShiftRangeSelection(item, { append: true });
        return;
      }
      toggleItemSelection(item, index);
      toggleShareBasket(item);
      setAnchorFromItem(item);
    };

    const handleItemDblClick = (item, index) => {
      if (!item || item.item_type === 'folder') return;
      selectedFolderNode.value = null;
      selectItem(item, index);
      fetchDetailStatus(item);
      isDetailExpanded.value = true;
    };

    // フォルダ行クリック=選択(動画行と同じ操作体系)。ダブルクリックで開く
    const handleFolderClick = (folderId, folderName, event) => {
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        selectFolder(folderId);
        return;
      }

      const node = (folders.value || []).find((f) => String(f.id) === String(folderId))
        || { id: folderId, name: folderName };

      if (!isFolderAllowed(folderId)) {
        selectedFolderNode.value = node;
        return;
      }

      const folderBasketItem = {
        item_type: 'folder',
        id: folderId,
        name: node.name,
        folder_path: getFolderPath(folderId),
      };

      if (event && event.shiftKey) {
        applyShiftRangeSelection(folderBasketItem, { append: event.ctrlKey || event.metaKey });
        return;
      }

      if (event && (event.ctrlKey || event.metaKey)) {
        toggleShareBasket(folderBasketItem);
        selectedFolderNode.value = isInShareBasket(folderId, 'folder') ? node : null;
        setAnchorFromItem(folderBasketItem);
        return;
      }

      selectItem(null);
      clearShareBasket();
      toggleShareBasket(folderBasketItem);
      selectedFolderNode.value = node;
      setAnchorFromItem(folderBasketItem);
    };

    // フォルダのチェックボックス選択でも詳細パネル表示を連動させる(動画チェックボックスと揃える)
    const handleFolderCheckboxClick = (folderId, folderName, event) => {
      const node = (folders.value || []).find((f) => String(f.id) === String(folderId))
        || { id: folderId, name: folderName };
      const basketItem = {
        item_type: 'folder',
        id: folderId,
        name: node.name,
        folder_path: getFolderPath(folderId),
      };

      if (event && event.shiftKey) {
        applyShiftRangeSelection(basketItem, { append: true });
        return;
      }

      toggleShareBasket(basketItem);
      if (isInShareBasket(folderId, 'folder')) {
        selectedFolderNode.value = node;
      }
      // OFF時は既存のwatch(shareBasketFolderIds)が表示中フォルダのみクリアする
      setAnchorFromItem(basketItem);
    };

    const handleFolderDblClick = (folderId) => {
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        return;
      }
      removeFromShareBasket(folderId, 'folder');
      selectedFolderNode.value = null;
      selectFolder(folderId);
    };

    const closeDetailPanel = () => {
      selectItem(null);
      selectedFolderNode.value = null;
    };

    const setUploading = (key, status) => {
      const normalizedKey = String(key ?? '');
      if (!normalizedKey) {
        return;
      }

      const next = new Set(uploadingKeys.value);
      if (status) {
        next.add(normalizedKey);
      } else {
        next.delete(normalizedKey);
      }

      uploadingKeys.value = next;
    };

    // 編集画面の kind セレクタ連動表示 (C12)
    const editKind = ref('');
    const onEditKindChange = (e) => {
      editKind.value = e.target.value;
    };
    // 動画作成/編集フォームの保存先フォルダ(ツリー選択の picker が管理)。
    // kind-upload-list へ既存/新規フォルダ判定用に渡す('' | 数値id | 'root')。
    const videoFormPicker = useVideoFormFolderPicker();
    const videoFormFolderId = computed(() => videoFormPicker.pickerFolderId.value);
    onMounted(() => {
      const kindInput = document.getElementById('kind');
      if (kindInput && kindInput.tagName === 'SELECT') {
        editKind.value = kindInput.value;
      }
      initAttachmentTarget();
    });

    const uploadingStatus = (status) => {
      setUploading('global', status);
    };

    // 動画作成/編集フォーム: kind-upload-list から公開された登録API(フォルダ経由アイテムの
    // サーバ登録)を保持する。@api-ready で受け取る(コンポーネントインスタンス全体は渡さない)。
    const folderUploadApiRef = { current: null };
    const setFolderUploadApi = (api) => {
      folderUploadApiRef.current = api;
    };
    const videoFormSubmitting = ref(false);

    const handleVideoFormSubmit = async (event) => {
      // 二重送信防止(フォルダ経由登録の fetch が完了するまで再度の submit を無視する)
      if (videoFormSubmitting.value) {
        event.preventDefault();
        return;
      }
      if (isUploading.value) {
        event.preventDefault();
        window.alert('アップロード中のため登録できません。アップロード完了後に登録してください。');
        return;
      }

      const videoForm = event.target;

      // 添付は attachments_{i}_* の 4 項目で 1 件。上限を超えた分はサーバーが黙って無視するため止める。
      // 登録画面・編集画面の両方で効かせる(どちらも添付ファイル欄を持つ)。
      const attachmentCount = videoForm.querySelectorAll('input[name^="attachments_"][name$="_filename"]').length;
      const maxAttachments = Number(videoForm.dataset.attachmentsMaxItems || 0);

      if (maxAttachments > 0 && attachmentCount > maxAttachments) {
        event.preventDefault();
        window.alert(`一度に添付できるファイル数を超えています。${maxAttachments}件以下にしてから実行してください。`);
        return;
      }

      const api = folderUploadApiRef.current;
      if (!api) {
        // 編集画面には kind-upload-list が無いため api は常に null で、ここからネイティブ
        // 送信(PUT)に落ちる。登録画面は JSON で送るため PHP の max_input_vars は掛からないが、
        // 編集画面はフォーム丸ごとの送信のため、ここでのみ max_input_vars の検査を行う。
        // hidden の uploads[i][*] はアップロード完了の待ち合わせ用に残しているだけで送信には使わない。
        const inputVarCount = videoForm.querySelectorAll('input[name], select[name], textarea[name]').length;
        const maxInputVars = Number(videoForm.dataset.maxInputVars || 0);
        if (maxInputVars > 0 && inputVarCount >= maxInputVars) {
          event.preventDefault();
          const maxUploadItems = Number(videoForm.dataset.uploadMaxItems || 0);
          window.alert(`一度に登録できるファイル数を超えています。${maxUploadItems}件以下にしてから実行してください。`);
          return;
        }
        // 従来通りネイティブ送信に任せる
        return;
      }

      // ここからは登録画面のみ。件数上限は component が保持する全ファイル件数で判定する。
      const maxUploadItems = Number(videoForm.dataset.uploadMaxItems || 0);
      if (maxUploadItems > 0 && api.itemCount() > maxUploadItems) {
        event.preventDefault();
        window.alert(`一度に登録できるファイル数を超えています。${maxUploadItems}件以下にしてから実行してください。`);
        return;
      }

      event.preventDefault();
      videoFormSubmitting.value = true;
      try {
        // 添付先ファイルを決める。
        // - 「自動」(選択肢が空文字) → 選択肢一覧の is_auto_target が立っているものを使う
        // - 明示選択 → その filename をそのまま使う
        let folderAttachmentTarget = null;
        const selected = attachmentTargetFilename.value;
        if (selected === '') {
          const autoOpt = attachmentTargetOptions.value.find((o) => o.is_auto_target);
          folderAttachmentTarget = autoOpt ? autoOpt.filename : null;
        } else {
          folderAttachmentTarget = selected;
        }

        const result = await api.registerFolderItems(event.target, folderAttachmentTarget);
        if (!result.ok) {
          // 失敗表示はコンポーネント側で完結しているためここでは何もしない
          videoFormSubmitting.value = false;
          return;
        }
        // 登録は JSON 分割送信 1 本に統一したため、store() へのネイティブ送信は行わない
        window.location.replace(event.target.dataset.folderUploadRedirect);
      } catch (e) {
        videoFormSubmitting.value = false;
      }
    };

    const createClip = () => {
      console.log('ClipCreator');
    };

    const toggleSidebar = () => {
      showSidebar.value = !showSidebar.value;
    };
    const closeMobileSearch = () => {
      isMobileSearchOpen.value = false;
    };
    const toggleMobileSearch = () => {
      isMobileSearchOpen.value = !isMobileSearchOpen.value;
    };
    const handleViewportResize = () => {
      if (window.innerWidth >= 1024) {
        isMobileSearchOpen.value = false;
      }
    };

    onMounted(() => {
      const { dataSource, shouldReset } = initDataSource();
      void videoFormPicker.init();
      setMoveOptions({
        endpoint: dataSource?.dataset?.moveEndpoint || '',
        failedMessage:
          dataSource?.dataset?.folderMoveFailedMessage || moveFailedMessage.value,
        folderEndpoint: dataSource?.dataset?.folderMoveEndpoint || '',
        intoSelfMessage: dataSource?.dataset?.folderMoveIntoSelfMessage || '',
        canMoveFolders: dataSource?.dataset?.canMoveFolders === '1',
      });
      applyRestoreCounts(initialRestoreCounts.value);

      if (shouldReset) {
        clearSelection();
        // Home (reset=1) で戻ったときは、遷移元が /dashboard でも開閉状態を復元せず全折りたたみにする (ユーザー決定 2026-10-02)
        collapseAllFolders();
        if (selectedFolderId.value) {
          expandAncestorsOf(selectedFolderId.value);
        }
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('reset');
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (error) {
          // noop: URL API not available
        }
      }

      const rawPermissions = dataSource?.getAttribute('data-current-folder-permissions') || '[]';
      try {
        const parsed = JSON.parse(rawPermissions);
        currentFolderPermissions.value = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        currentFolderPermissions.value = [];
      }

      initViewMode(dataSource);
      initFavoritePreference(dataSource);
      initTopPreference(dataSource);
      filterPanelOpen.value = dataSource?.getAttribute('data-filter-panel-open') === '1';
      initFolderPins(dataSource);
      favoritesFolderLabel.value =
        dataSource?.dataset?.favoritesLabel || favoritesFolderLabel.value;
      removeDashboardParamsFromAddressBar();

      if (favoriteOnly.value) {
        selectedFolderId.value = FAVORITES_FOLDER_ID;
      }

      if (!shouldReset && !hasFocusIdInUrl() && !favoriteOnly.value && !topOnly.value) {
        restoreSelection({ itemOnly: hasFolderIdInUrl() });
        // URL 指定のフォルダを localStorage にも保存し、後の restoreSelection で古いフォルダが復元されないようにする
        if (hasFolderIdInUrl() && selectedFolderId.value) {
          storeFolderSelection(selectedFolderId.value);
        }
      }
      ensureDefaultFolder();
      syncFolderIdFields(searchScope.value);
      initDomEnhancements();
      useNavDrawer();
      initPaneLayout();
      initRestoreBarLayout();
      initToolbarOverflow();
      initListHeaderSync();
      initSearchMatches(dataSource);
      initBodyText(dataSource);
      initCadInfo(dataSource);
      void applyFocusedVideoSelectionFromUrl();
      window.addEventListener('resize', handleViewportResize);
      window.addEventListener('pageshow', () => {
        setTimeout(() => {
          const ds = document.getElementById('dashboard-data-source');
          const kw = ds?.getAttribute('data-active-keyword') || '';
          document.querySelectorAll('input[name="q"]').forEach((input) => {
            input.value = kw;
          });
        }, 0);
      });
    });

    onBeforeUnmount(() => {
      teardownPaneLayout();
      teardownRestoreBarLayout();
      teardownToolbarOverflow();
      teardownListHeaderSync();
      teardownSearchMatches();
      window.removeEventListener('resize', handleViewportResize);
    });

    const pdfBlobUrl = ref(null);
    const onPdfBlobReady = (url) => {
      pdfBlobUrl.value = url;
    };

    const previewPdfModalOpen = ref(false);
    const handlePreviewPdfModalShow = () => {
      previewPdfModalOpen.value = true;
    };
    const handlePreviewPdfModalHide = () => {
      previewPdfModalOpen.value = false;
    };

    onMounted(() => {
      const el = document.getElementById('previewPdfModal');
      if (el && window.jQuery) {
        window.jQuery(el).on('show.bs.modal', handlePreviewPdfModalShow);
        window.jQuery(el).on('hidden.bs.modal', handlePreviewPdfModalHide);
      }
    });

    watch(selectedItemRef, (nextItem) => {
      pdfBlobUrl.value = null;
      if (nextItem) {
        detailSubTab.value = 'info';
      } else {
        closeDetailExpand();
      }
    });

    const visibleItems = computed(() => {
      // Server-side filtered, so we just return everything
      return items.value;
    });
    const {
      openedMatchVideoId,
      matchPanelActiveItem,
      matchPanelItems,
      matchPanelTruncated,
      matchPanelLoading,
      matchPanelInsertIndex,
      activeDetailPage,
      toggleMatchPanel,
      prefetchMatchPanel,
      closeMatchPanel,
      handleMatchLabelClick,
      applyPageNumber,
      initSearchMatches,
      teardownSearchMatches,
    } = useSearchMatches({
      visibleItems,
      activeKeyword,
      selectedItem: selectedItemRef,
      handleItemClick,
      detailPlayerRef,
      jumpToHit,
    });
    const { detailPreviewPage, handleDetailPageChange } = useDetailPreviewPage({
      selectedItem: selectedItemRef,
    });
    const {
      bodyTextItems,
      bodyTextFrom,
      bodyTextTo,
      bodyTextTotalPages,
      bodyTextLoading,
      bodyTextHasPrev,
      bodyTextHasNext,
      goToBodyTextPrev,
      goToBodyTextNext,
      jumpToBodyPage,
      initBodyText,
      ensureBodyPageLoaded,
    } = useBodyText({ selectedItem: selectedItemRef, detailSubTab, applyPageNumber });
    // useDetailPaneJump 生成時点ではまだ useBodyText が無いため、ここで実体を差し込む
    ensureBodyPageRef.value = ensureBodyPageLoaded;
    const {
      cadInfoViews,
      cadInfoTags,
      cadInfoLoading,
      isCadGroupOpen,
      toggleCadGroup,
      initCadInfo,
      ensureCadInfoLoaded,
    } = useCadInfo({ selectedItem: selectedItemRef, detailSubTab });
    // useDetailPaneJump 生成時点ではまだ useCadInfo が無いため、ここで実体を差し込む
    ensureCadInfoRef.value = ensureCadInfoLoaded;
    const globalRestoringCount = computed(() =>
      Number(restoreCountsState.value?.global?.restoring?.count || 0)
    );
    const globalRestoredCount = computed(() =>
      Number(restoreCountsState.value?.global?.restored?.count || 0)
    );
    const globalRestoringDisplayIds = computed(
      () => restoreCountsState.value?.global?.restoring?.display_ids || []
    );
    const globalRestoringMore = computed(() =>
      Number(restoreCountsState.value?.global?.restoring?.more || 0)
    );
    const globalRestoredDisplayIds = computed(
      () => restoreCountsState.value?.global?.restored?.display_ids || []
    );
    const globalRestoredMore = computed(() =>
      Number(restoreCountsState.value?.global?.restored?.more || 0)
    );
    const currentRestoringCount = computed(() =>
      Number(restoreCountsState.value?.current?.restoring?.count || 0)
    );
    const currentRestoredCount = computed(() =>
      Number(restoreCountsState.value?.current?.restored?.count || 0)
    );
    const currentRestoringDisplayIds = computed(
      () => restoreCountsState.value?.current?.restoring?.display_ids || []
    );
    const currentRestoringMore = computed(() =>
      Number(restoreCountsState.value?.current?.restoring?.more || 0)
    );
    const currentRestoredDisplayIds = computed(
      () => restoreCountsState.value?.current?.restored?.display_ids || []
    );
    const currentRestoredMore = computed(() =>
      Number(restoreCountsState.value?.current?.restored?.more || 0)
    );

    // 完全復元専用カウント computed
    const globalFullRestoringCount = computed(() =>
      Number(restoreCountsState.value?.global?.full_restoring?.count || 0)
    );
    const globalFullRestoringDisplayIds = computed(
      () => restoreCountsState.value?.global?.full_restoring?.display_ids || []
    );
    const globalFullRestoringMore = computed(() =>
      Number(restoreCountsState.value?.global?.full_restoring?.more || 0)
    );
    const globalFullRestoredCount = computed(() =>
      Number(restoreCountsState.value?.global?.full_restored?.count || 0)
    );
    const globalFullRestoredDisplayIds = computed(
      () => restoreCountsState.value?.global?.full_restored?.display_ids || []
    );
    const globalFullRestoredMore = computed(() =>
      Number(restoreCountsState.value?.global?.full_restored?.more || 0)
    );
    const globalFullRestoreFailedCount = computed(() =>
      Number(restoreCountsState.value?.global?.full_restore_failed?.count || 0)
    );
    const globalFullRestoreFailedDisplayIds = computed(
      () => restoreCountsState.value?.global?.full_restore_failed?.display_ids || []
    );
    const globalFullRestoreFailedMore = computed(() =>
      Number(restoreCountsState.value?.global?.full_restore_failed?.more || 0)
    );
    const currentFullRestoringCount = computed(() =>
      Number(restoreCountsState.value?.current?.full_restoring?.count || 0)
    );
    const currentFullRestoringDisplayIds = computed(
      () => restoreCountsState.value?.current?.full_restoring?.display_ids || []
    );
    const currentFullRestoringMore = computed(() =>
      Number(restoreCountsState.value?.current?.full_restoring?.more || 0)
    );
    const currentFullRestoredCount = computed(() =>
      Number(restoreCountsState.value?.current?.full_restored?.count || 0)
    );
    const currentFullRestoredDisplayIds = computed(
      () => restoreCountsState.value?.current?.full_restored?.display_ids || []
    );
    const currentFullRestoredMore = computed(() =>
      Number(restoreCountsState.value?.current?.full_restored?.more || 0)
    );
    const currentFullRestoreFailedCount = computed(() =>
      Number(restoreCountsState.value?.current?.full_restore_failed?.count || 0)
    );
    const currentFullRestoreFailedDisplayIds = computed(
      () => restoreCountsState.value?.current?.full_restore_failed?.display_ids || []
    );
    const currentFullRestoreFailedMore = computed(() =>
      Number(restoreCountsState.value?.current?.full_restore_failed?.more || 0)
    );

    const folderCreate = useFolderCreate({
      folders,
      selectedFolderId,
      allowedFolderIds,
      onCreated: (newFolder) => {
        if (newFolder && newFolder.id !== undefined && newFolder.id !== null) {
          const list = Array.isArray(folders.value) ? folders.value : [];
          const exists = list.some((f) => String(f.id) === String(newFolder.id));
          if (!exists) {
            folders.value = sortFoldersByHierarchy([...list, newFolder]);
          }
          // レビュー指摘#7: 非adminが作成した新フォルダをリロードなしで操作可能にするため許可ID一覧へ即時追加する
          // (FolderNode.vueのincludesは厳密比較のためNumberで揃える。admin=nullはスキップ)
          if (Array.isArray(allowedFolderIds.value)) {
            const numericId = Number(newFolder.id);
            if (!allowedFolderIds.value.some((id) => Number(id) === numericId)) {
              allowedFolderIds.value = [...allowedFolderIds.value, numericId];
            }
          }
          if (typeof selectFolder === 'function') {
            selectFolder(newFolder.id);
          }
        }
        if (window.jQuery) {
          window.jQuery('#folderCreateModal').modal('hide');
        }
      },
    });

    const openFolderCreateModal = () => {
      folderCreate.open();
      if (window.jQuery) {
        window.jQuery('#folderCreateModal').modal('show');
      }
    };

    onMounted(() => {
      const folderCreateModalEl = document.getElementById('folderCreateModal');
      if (folderCreateModalEl && window.jQuery) {
        window.jQuery(folderCreateModalEl).on('shown.bs.modal', () => {
          folderCreate.scrollToSelectedParent();
        });
      }

      const trashRestoreModalEl = document.getElementById('trashRestoreModal');
      if (trashRestoreModalEl && window.jQuery) {
        window.jQuery(trashRestoreModalEl).on('shown.bs.modal', () => {
          scrollToSelectedTrashRestoreFolder();
        });
      }
    });

    const {
      bulkDeleteMode,
      bulkHasVersionedItems,
      bulkVersionedItems,
      bulkVersionsLoading,
      bulkVersionsError,
      bulkExtraVersionEntries,
      bulkExtraVersionIds,
      bulkTrashTotalCount,
      bulkTrashMaxCount,
      bulkTrashLimitExceeded,
      fetchBulkVersionGroups,
      showBulkTrashModal,
      submitBulkTrash,
    } = useBulkTrash(shareBasketCount, clearShareBasket, shareBasketItemsList, shareBasketVideoIds, maxInputVars, bulkMaxItems);

    const {
      selectionArchives,
      selectionArchiveDownloadableCount,
      selectionArchiveEstimate,
      selectionArchiveEstimateLoading,
      selectionArchiveIncludeSubfolders,
      selectionArchiveCreating,
      selectionArchiveCreatedNotice,
      selectionArchiveErrorMessage,
      selectionArchivePollTimeoutReached,
      selectionArchiveHasFolders,
      selectionArchiveCanCreate,
      selectionArchiveItems,
      showSelectionArchiveModal,
      createSelectionArchive,
      removeSelectionArchiveItem,
      formatArchiveText,
    } = useSelectionArchive({
      videoIds: shareBasketVideoIds,
      folderIds: shareBasketFolderIds,
      itemsList: shareBasketItemsList,
      count: shareBasketCount,
      removeFromBasket: removeFromShareBasket,
      clearBasket: clearShareBasket,
    });

    // 表示の切り替え(昇降の切り替えの有無)・選択数の札で、中央の幅が変わらずにボタンの幅が変わるため、描画後に決め直す
    watch(
      [viewMode, shareBasketCount, shareBasketFileCount, selectionArchiveDownloadableCount],
      () => {
        nextTick(() => {
          checkToolbarOverflow();
        });
      }
    );

    const {
      sourceDeleteMode,
      sourceDeleteTarget,
      sourceDeleteBulkEligibleItems,
      sourceDeleteBulkExcludedItems,
      sourceDeleteBulkMaxCount,
      sourceDeleteBulkLimitExceeded,
      showSourceDeleteModal,
      showBulkSourceDeleteModal,
      submitSourceDelete,
    } = useSourceDelete(shareBasketCount, clearShareBasket, shareBasketItemsList, shareBasketVideoIds, maxInputVars, bulkMaxItems);

    const {
      folderBulkDeleteLoading,
      folderBulkDeleteExecuting,
      folderBulkDeleteDeletable,
      folderBulkDeleteBlocked,
      folderBulkDeleteErrorMessage,
      folderBulkDeleteResultMessage,
      openFolderBulkDeleteModal,
      removeFolderFromBulkDelete,
      executeFolderBulkDelete,
    } = useFolderBulkDelete({
      folders,
      items,
      selectedFolderId,
      basketFolderIds: shareBasketFolderIds,
      removeFromBasket: removeFromShareBasket,
      selectFolder,
      onMessage: (message) => {
        directLinkFlashMessage.value = message;
        directLinkFlashIsError.value = false;
        directLinkFlashKey.value += 1;
      },
    });

    const {
      trashRestoreFolderId,
      trashRestoreFolderMissing,
      trashRestoreAction,
      trashRestoreTopLevelFolders,
      trashRestoreGetChildren,
      trashRestoreIsCollapsed,
      trashRestoreToggleFolder,
      trashRestoreSelectFolder,
      trashRestoreSelectRoot,
      trashRestoreIsRootSelected,
      openTrashRestoreModal,
      scrollToSelectedTrashRestoreFolder,
    } = useTrashRestore({ folders });

    const { confirmClipTrashRestoreSubmit } = useClipTrashRestore();

    const {
      bulkTagsError,
      bulkTagsLimitExceeded,
      bulkTagsLimitMaxFiles,
      bulkTagEligibleItems,
      bulkTagIneligibleItems,
      showBulkTagModal,
      submitBulkTags,
    } = useBulkTags(shareBasketCount, clearShareBasket, shareBasketItemsList, maxInputVars, bulkMaxItems);

    const {
      showBulkAiAnalysisModal,
      submitBulkAiAnalysis,
      reanalyzeConfirmed,
      bulkAiStatusOf,
      bulkAiReanalyzeCount,
      bulkAiNewCount,
      bulkAiCanSubmit,
    } = useBulkAiAnalysis(shareBasketCount, clearShareBasket, shareBasketFileItemsList);

    const currentPageSelectableItems = computed(() => {
      const list = Array.isArray(items.value) ? items.value : [];
      const fileItems = list.filter((i) => i && i.item_type !== 'folder');
      const folderItems = list
        .filter((i) => i && i.item_type === 'folder')
        .filter((f) => isFolderAllowed(f.id))
        .map((f) => ({ item_type: 'folder', id: f.id, name: f.name, folder_path: getFolderPath(f.id) }));
      return [...folderItems, ...fileItems];
    });
    const isAllCurrentPageSelected = computed(() =>
      currentPageSelectableItems.value.length > 0 &&
      currentPageSelectableItems.value.every((i) => isInShareBasket(i.id, i.item_type === 'folder' ? 'folder' : 'video')));
    const toggleSelectAllCurrentPage = () => {
      if (isAllCurrentPageSelected.value) {
        currentPageSelectableItems.value.forEach((i) => removeFromShareBasket(i.id, i.item_type === 'folder' ? 'folder' : 'video'));
      } else {
        setShareBasketItems(currentPageSelectableItems.value, { append: true });
      }
    };

    return {
      isUploading,
      isSubmit,
      attachmentArchives,
      items,
      folders,
      allowedFolderIds,
      viewMode,
      filterPanelOpen,
      favoriteOnly,
      topOnly,
      visibleItems,
      currentFolderItemCount,
      currentFolderItemCountLabel,
      globalRestoringCount,
      globalRestoredCount,
      globalRestoringDisplayIds,
      globalRestoringMore,
      globalRestoredDisplayIds,
      globalRestoredMore,
      currentRestoringCount,
      currentRestoredCount,
      currentRestoringDisplayIds,
      currentRestoringMore,
      currentRestoredDisplayIds,
      currentRestoredMore,
      globalFullRestoringCount,
      globalFullRestoringDisplayIds,
      globalFullRestoringMore,
      globalFullRestoredCount,
      globalFullRestoredDisplayIds,
      globalFullRestoredMore,
      globalFullRestoreFailedCount,
      globalFullRestoreFailedDisplayIds,
      globalFullRestoreFailedMore,
      currentFullRestoringCount,
      currentFullRestoringDisplayIds,
      currentFullRestoringMore,
      currentFullRestoredCount,
      currentFullRestoredDisplayIds,
      currentFullRestoredMore,
      currentFullRestoreFailedCount,
      currentFullRestoreFailedDisplayIds,
      currentFullRestoreFailedMore,
      isFolderAllowed,
      getFolderPath,
      currentFolderMeta,
      currentFolderPermissions,
      activeBreadcrumb,
      activeBreadcrumbItems,
      searchScope,
      activeKeyword,
      canUseFulltextSearch,
      openedMatchVideoId,
      matchPanelActiveItem,
      matchPanelItems,
      matchPanelTruncated,
      matchPanelLoading,
      matchPanelInsertIndex,
      activeDetailPage,
      detailPreviewPage,
      handleDetailPageChange,
      toggleMatchPanel,
      prefetchMatchPanel,
      closeMatchPanel,
      handleMatchLabelClick,
      bodyTextItems,
      bodyTextFrom,
      bodyTextTo,
      bodyTextTotalPages,
      bodyTextLoading,
      bodyTextHasPrev,
      bodyTextHasNext,
      goToBodyTextPrev,
      goToBodyTextNext,
      jumpToBodyPage,
      cadInfoViews,
      cadInfoTags,
      cadInfoLoading,
      isCadGroupOpen,
      toggleCadGroup,
      mobileCurrentFolderName,
      mobileBreadcrumbDisplay,
      topLevelFolders,
      pinnedFolders,
      pinnedFolderIds,
      pinnedFolderSectionStyle,
      getChildren,
      isCollapsed,
      toggleFolder,
      expandAllFolders,
      collapseAllFolders,
      isAllFoldersCollapsed,
      toggleAllFolders,
      selectedItem: selectedItemRef,
      selectedIds,
      selectedFolderId,
      currentFolderId,
      pdfBlobUrl,
      onPdfBlobReady,
      previewPdfModalOpen,
      setUploading,
      uploadingStatus,
      handleVideoFormSubmit,
      setFolderUploadApi,
      attachmentTargetOptions,
      attachmentTargetFilename,
      attachmentTargetAutoLabel,
      setAttachmentTargetOptions,
      setAttachmentCount,
      showAttachmentTargetSelect,
      editKind,
      onEditKindChange,
      videoFormFolderId,
      videoFormPickerTopLevelFolders: videoFormPicker.topLevelFolders,
      videoFormPickerGetChildren: videoFormPicker.getChildren,
      videoFormPickerIsCollapsed: videoFormPicker.isCollapsed,
      videoFormPickerToggleFolder: videoFormPicker.toggleFolder,
      videoFormPickerSelectFolder: videoFormPicker.selectFolder,
      videoFormPickerSelectRoot: videoFormPicker.selectRoot,
      videoFormPickerFolderId: videoFormPicker.pickerFolderId,
      videoFormPickerAllowedFolderIds: videoFormPicker.allowedFolderIds,
      videoFormPickerIsRootSelected: videoFormPicker.isRootSelected,
      videoFormPickerInputValue: videoFormPicker.inputValue,
      createClip,
      setViewMode,
      toggleSeqOrder,
      seqOrder,
      sortKey,
      setSortOrder,
      toggleFavorite,
      isFavoriteToggling,
      toggleFolderPin,
      isPinProcessing,
      canToggleCurrentFolderPin,
      isCurrentFolderPinned,
      isCurrentFolderPinProcessing,
      toggleCurrentFolderPin,
      selectItem,
      handleItemClick,
      handleCheckboxClick,
      handleItemDblClick,
      selectedFolderNode,
      handleFolderClick,
      handleFolderCheckboxClick,
      handleFolderDblClick,
      closeDetailPanel,
      handleDragStart,
      handleDragEnd,
      handleFolderDrop,
      handleFolderDragEnter,
      handleFolderDragLeave,
      handleTopDrop,
      handleTopDragEnter,
      handleTopDragLeave,
      dragTargetFolderId,
      isTopDropTarget,
      noDropFolderIds,
      isDragging,
      selectFolder,
      getFolderPathItems,
      selectFavoritesFolder,
      selectTopFolder,
      showSidebar,
      toggleSidebar,
      isMobileSearchOpen,
      toggleMobileSearch,
      closeMobileSearch,
      openAttachmentDownloadModal,
      downloadAllArchives,
      exportToOneDrive,
      exportToSharePoint,
      exportToGoogleDrive,
      exportToDropbox,
      exportToBox,
      isExporting,
      deleteTargetItem,
      deleteMode,
      deleteVersionsList,
      deleteVersionsLoading,
      showDeleteModal,
      handleVersionSelect,
      confirmDelete,
      searchByRestoreId,
      detailSubTab,
      detailPlayerRef,
      aiAnalysisData,
      aiAnalysisLoading,
      aiAnalysisError,
      formatAiSeconds,
      sceneThumbnailPreviewUrl,
      openSceneThumbnailPreview,
      seekToTranscriptSegment,
      isJumpableHit,
      scrollToCadView,
      isDetailExpanded,
      toggleDetailExpand,
      closeDetailExpand,
      shareBasketCount,
      shareBasketVideoIds,
      shareBasketFolderIds,
      shareBasketFileCount,
      shareBasketFolderCount,
      shareBasketItemsList,
      shareBasketFileItemsList,
      toggleShareBasket,
      isInShareBasket,
      clearShareBasket,
      removeFromShareBasket,
      directLinkFlashMessage,
      directLinkFlashIsError,
      directLinkFlashKey,
      copyDirectLinkFile,
      copyDirectLinkFolder,
      folderCreateParentId: folderCreate.parentId,
      folderCreateFolderName: folderCreate.folderName,
      folderCreateSubmitting: folderCreate.submitting,
      folderCreateErrors: folderCreate.errors,
      folderCreateTopLevelFolders: folderCreate.treeTopLevelFolders,
      folderCreateGetChildren: folderCreate.treeGetChildren,
      folderCreateIsCollapsed: folderCreate.treeIsCollapsed,
      folderCreateToggleFolder: folderCreate.treeToggleFolder,
      folderCreateSelectParent: folderCreate.selectParent,
      openFolderCreateModal,
      submitFolderCreate: folderCreate.submit,
      folderBulkDeleteLoading,
      folderBulkDeleteExecuting,
      folderBulkDeleteDeletable,
      folderBulkDeleteBlocked,
      folderBulkDeleteErrorMessage,
      folderBulkDeleteResultMessage,
      openFolderBulkDeleteModal,
      removeFolderFromBulkDelete,
      executeFolderBulkDelete,
      trashRestoreFolderId,
      trashRestoreFolderMissing,
      trashRestoreAction,
      trashRestoreTopLevelFolders,
      trashRestoreGetChildren,
      trashRestoreIsCollapsed,
      trashRestoreToggleFolder,
      trashRestoreSelectFolder,
      trashRestoreSelectRoot,
      trashRestoreIsRootSelected,
      openTrashRestoreModal,
      scrollToSelectedTrashRestoreFolder,
      confirmClipTrashRestoreSubmit,
      showBulkTrashModal,
      selectionArchives,
      selectionArchiveDownloadableCount,
      selectionArchiveEstimate,
      selectionArchiveEstimateLoading,
      selectionArchiveIncludeSubfolders,
      selectionArchiveCreating,
      selectionArchiveCreatedNotice,
      selectionArchiveErrorMessage,
      selectionArchivePollTimeoutReached,
      selectionArchiveHasFolders,
      selectionArchiveCanCreate,
      selectionArchiveItems,
      showSelectionArchiveModal,
      createSelectionArchive,
      removeSelectionArchiveItem,
      formatArchiveText,
      submitBulkTrash,
      bulkDeleteMode,
      bulkHasVersionedItems,
      bulkVersionedItems,
      bulkVersionsLoading,
      bulkVersionsError,
      bulkExtraVersionEntries,
      bulkExtraVersionIds,
      bulkTrashTotalCount,
      bulkTrashMaxCount,
      bulkTrashLimitExceeded,
      fetchBulkVersionGroups,
      sourceDeleteMode,
      sourceDeleteTarget,
      sourceDeleteBulkEligibleItems,
      sourceDeleteBulkExcludedItems,
      sourceDeleteBulkMaxCount,
      sourceDeleteBulkLimitExceeded,
      showSourceDeleteModal,
      showBulkSourceDeleteModal,
      submitSourceDelete,
      bulkTagsError,
      bulkTagsLimitExceeded,
      bulkTagsLimitMaxFiles,
      bulkTagEligibleItems,
      bulkTagIneligibleItems,
      showBulkTagModal,
      submitBulkTags,
      showBulkAiAnalysisModal,
      submitBulkAiAnalysis,
      reanalyzeConfirmed,
      bulkAiStatusOf,
      bulkAiReanalyzeCount,
      bulkAiNewCount,
      bulkAiCanSubmit,
      currentPageSelectableItems,
      isAllCurrentPageSelected,
      toggleSelectAllCurrentPage,
    };
  },
});

// グローバルプロパティとして `__` を登録
app.config.globalProperties.__ = (str) => _.get(window.i18n, str);

// コンポーネント登録
app.component('flush', FlushMessage);
app.component('upload-form', UploadFormComponent);
app.component('datetime', DateTimeComponent);
app.component('tagselect', TagAutoInputComponent);
app.component('aiattributefilter', AiAttributeFilterComponent);
app.component('userselect', UserSelectComponent);
app.component('multi-upload-form', MultiUploadFormComponent);
app.component('kind-upload-list', KindUploadListComponent);
app.component('player', Player);
app.component('clip-creator', ClipCreatorComponent);
app.component('clip-modal', ClipModalComponent);
app.component('clip-show-modal', ClipShowModalComponent);
app.component('expire-date', ExpireDateComponent);
app.component('area-tag', AreaTagComponent);
app.component('shareUrl', ShareUrlComponent);
app.component('videojs', Videojs);
app.component('thumbnail-selector', ThumbnailSelector);
app.component('fast-dl', FastDownload);
app.component('selection-archive-list', SelectionArchiveList);
app.component('pdf-preview-pdfjs', PdfPreviewPdfjs);
app.component('cad-viewer-aps', CadViewerAps);
app.component('share-preview-modal', SharePreviewModal);
app.component('folder-node', FolderNode);
app.component('folder-picker-tree', FolderPickerTree);
app.component('folder-list-table', FolderListTable);
app.component('version-history-tab', VersionHistoryTab);
app.component('grid-tag-badges', GridTagBadges);
app.component('folder-archive-panel', FolderArchivePanel);
app.component('highlighted-text', HighlightedText);

// Vue アプリをマウント
app.mount('#app');

const initCustomNumberSpinner = () => {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-spin-action]');
    if (!button) {
      return;
    }

    const wrapper = button.closest('.number-spin-field');
    const input = wrapper?.querySelector('input[type="number"]');
    if (!input || input.disabled || input.readOnly) {
      return;
    }

    event.preventDefault();

    if (button.dataset.spinAction === 'up') {
      input.stepUp();
    } else if (button.dataset.spinAction === 'down') {
      input.stepDown();
    } else {
      return;
    }

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.focus();
  });
};

document.addEventListener('DOMContentLoaded', () => {
  bootPageModules();
  initCustomNumberSpinner();
});
